<template>
  <div ref="dropdownRef" :class="$style.dropdown">
    <div
      ref="triggerRef"
      :class="[$style.trigger, isOpenModel && $style.triggerOpen]"
      @mouseenter="props.trigger === 'hover' ? open() : null"
      @mouseleave="props.trigger === 'hover' ? close() : null"
      @click.capture="handleTriggerClick"
      @pointerdown.passive="handlePointerDown"
      @touchstart.passive="handleTouchStart"
      @touchmove.passive="handleTouchMove"
      @touchend="handleTouchEnd"
      @contextmenu="handleContextMenu"
    >
      <slot
        name="trigger"
        :target="popoverId"
        :is-open="isOpenModel"
        :toggle-popover="togglePopover"
        :popover="popoverRef"
        @mouseenter="props.trigger === 'hover' ? open() : null"
        @mouseleave="props.trigger === 'hover' ? close() : null"
      >
        <SCAIconButton
          :popovertarget="popoverId"
          size="small"
          type="ghost"
          appearance="secondary"
          :icon="TRIGGER_ICONS[triggerAlign]"
        />
      </slot>
    </div>
    <div
      :id="popoverId"
      ref="popoverRef"
      :class="[
        $style.popover,
        lessBorder && $style.lessBorder,
        noBorder && $style.noBorder,
        (noPadding || paddingUpset) && $style.noPadding,
        shadowVariant === 'upward' && $style.shadowUpward,
        enableTransition && !scrolling && $style.withTransition,
        noMobileMargin && $style.noMobileMargin,
        isPositioning && $style.positioning,
      ]"
      popover
      :data-popover-open="isOpenModel || undefined"
      :style="{ transform: floatingStyles.transform }"
      @beforetoggle="beforetoggle"
      @toggle="aftertoggle"
      @mouseenter="props.trigger === 'hover' ? open() : null"
      @mouseleave="props.trigger === 'hover' ? close() : null"
    >
      <template v-if="isOpenModel">
        <div
          :class="[$style.inner, justOpened && $style.animate]"
          :style="props.width ? { width: props.width } : undefined"
        >
          <SCASvgIcon
            v-if="scrollUp"
            :class="[$style.scrollIconTop, { [$style.hidden]: topVisible }]"
            icon="phosphor/caret-up"
            size="16px"
          />
          <div :class="[$style.content, paddingUpset && $style.paddingUnset, morePadding && $style.morePadding]">
            <div ref="topRef" />
            <div :class="$style.items" :style="props.gap ? { gap: `${props.gap}px` } : undefined">
              <slot :popover="popoverRef" />
            </div>
            <div ref="bottomRef" />
          </div>
          <SCASvgIcon
            v-if="scrollDown"
            :class="[$style.scrollIconBottom, { [$style.hidden]: bottomVisible }]"
            icon="phosphor/caret-down"
            size="16px"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Placement } from "@floating-ui/vue";
import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/vue";
import { useElementVisibility, useVModel } from "@vueuse/core";
import { v4 as uuid } from "uuid";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import type { IconName } from "../icons/iconNames.generated";
import SCAIconButton from "../SCAIconButton.vue";
import SCASvgIcon from "../SCASvgIcon.vue";
import { findDropdownAnchor } from "./dropdownAnchor";
import { isSidePlacement, marginPadding } from "./dropdownPlacement";
import { useScrolling } from "./useScrolling";

const TRIGGER_ICONS = {
  horizontal: "phosphor/dots-three",
  vertical: "phosphor/dots-three-vertical",
} satisfies Record<"horizontal" | "vertical", IconName>;

const props = withDefaults(
  defineProps<{
    placement?: Placement;
    offsetX?: number;
    offsetY?: number;
    matchTriggerWidth?: "exact" | "min" | "max";
    scrollUp?: boolean;
    scrollDown?: boolean;
    triggerAlign?: "horizontal" | "vertical";
    isOpen?: boolean;
    lessBorder?: boolean;
    noBorder?: boolean;
    noPadding?: boolean;
    morePadding?: boolean;
    shadowVariant?: "default" | "upward";
    trigger?: "click" | "hover";
    noMobileMargin?: boolean;
    toolbarOffset?: boolean;
    width?: string;
    paddingUpset?: boolean;
    gap?: number;
  }>(),
  {
    placement: "bottom-start",
    offsetX: undefined,
    offsetY: undefined,
    matchTriggerWidth: undefined,
    scrollUp: false,
    scrollDown: false,
    triggerAlign: "horizontal",
    isOpen: false,
    lessBorder: false,
    noBorder: false,
    noPadding: false,
    morePadding: false,
    shadowVariant: "default",
    trigger: "click",
    noMobileMargin: false,
    toolbarOffset: false,
    width: undefined,
    gap: undefined,
  },
);

const emit = defineEmits<{
  opened: [];
  closed: [];
  positioned: [];
  "update:isOpen": [isOpen: boolean];
}>();

const TRANSTION_DURATION = 300;

const popoverId = uuid();
const popoverRef = ref<HTMLElement>();
const triggerRef = ref<HTMLElement>();
const dropdownRef = ref<HTMLElement>();
const topRef = ref<HTMLDivElement>();
const bottomRef = ref<HTMLDivElement>();
const isOpenModel = useVModel(props, "isOpen", emit, { passive: true });
const topVisible = useElementVisibility(topRef);
const bottomVisible = useElementVisibility(bottomRef);
const justOpened = ref(false);
const enableTransition = ref(false);
const isPositioning = ref(false);
const { scrolling } = useScrolling({ active: isOpenModel, ignoreWithin: popoverRef });
let closeTimeout: ReturnType<typeof setTimeout> | undefined;
let justOpenedTimeout: ReturnType<typeof setTimeout> | undefined;
let transitionTimeout: ReturnType<typeof setTimeout> | undefined;

const longPressTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const touchStartTime = ref<number | null>(null);
const touchMoved = ref(false);
const isTouchInteraction = ref(false);

function hideNow() {
  if (!isOpenModel.value) return;
  popoverRef.value?.hidePopover();
}

defineExpose({
  popoverId,
  hideNow,
});

function togglePopover(options?: { force?: boolean }) {
  popoverRef.value?.togglePopover(options?.force);
}

function beforetoggle(event: ToggleEvent) {
  if (event.newState === "open") isPositioning.value = true;
}

function aftertoggle(event: ToggleEvent) {
  isOpenModel.value = event.newState === "open";

  if (event.newState === "closed") isPositioning.value = false;
  if (event.newState === event.oldState) {
    if (event.newState === "open") isPositioning.value = false;
    return;
  }
  if (event.newState === "open") {
    emit("opened");
    transitionTimeout = setTimeout(() => (enableTransition.value = true), TRANSTION_DURATION);
    return;
  }
  clearTimeout(transitionTimeout);
  justOpened.value = false;
  emit("closed");
  enableTransition.value = false;
}

watch(isOpenModel, (isOpen) => {
  clearTimeout(justOpenedTimeout);
  justOpened.value = isOpen;
  if (isOpen) justOpenedTimeout = setTimeout(() => (justOpened.value = false), TRANSTION_DURATION);
});

const triggerMatchProp = computed(() => {
  if (props.matchTriggerWidth === "exact") {
    return "width";
  } else if (props.matchTriggerWidth === "min") {
    return "min-width";
  } else if (props.matchTriggerWidth === "max") {
    return "max-width";
  } else {
    return undefined;
  }
});

const open = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = undefined;
  }
  popoverRef.value?.showPopover();
};

const close = () => {
  if (closeTimeout) clearTimeout(closeTimeout);
  closeTimeout = setTimeout(() => {
    popoverRef.value?.hidePopover();
  }, 200);
};

const placement = computed(() => props.placement);

function clearComputedSize() {
  const el = popoverRef.value;
  if (!el) return;
  el.style.maxHeight = "";
  el.style.width = "";
  el.style.minWidth = "";
  el.style.maxWidth = "";
}

const VIEWPORT_GUTTER = 8;

const middleware = computed(() => {
  const mainAxisOffset = props.offsetY ?? (props.toolbarOffset ? 7.5 : undefined);

  return [
    offset({ mainAxis: mainAxisOffset, crossAxis: props.offsetX }),
    flip({
      fallbackStrategy: "bestFit",
    }),
    // A side submenu on a narrow screen fits neither side; shifting it sideways keeps it on screen.
    shift((state) => ({
      crossAxis: isSidePlacement(props.placement),
      padding: { ...marginPadding(state), left: VIEWPORT_GUTTER, right: VIEWPORT_GUTTER },
    })),
    size((state) => ({
      padding: marginPadding(state),
      apply({ rects, elements, availableHeight }) {
        if (triggerMatchProp.value) {
          Object.assign(elements.floating.style, {
            [triggerMatchProp.value]: `${rects.reference.width}px`,
          });
        }
        Object.assign(elements.floating.style, {
          maxHeight: `${Math.max(0, availableHeight)}px`,
        });
      },
    })),
  ];
});

// An editable grid cell marks its field box as the anchor, so the popover opens under the cell's
// underline rather than under the trigger's own, shorter box. Re-read on open as well as on mount,
// because a trigger can be mounted before it is inserted where the mark is.
const anchorRef = ref<HTMLElement | null>(null);
const referenceRef = computed(() => anchorRef.value ?? dropdownRef.value);

function resolveAnchor() {
  anchorRef.value = findDropdownAnchor(dropdownRef.value);
}

onMounted(resolveAnchor);

const {
  x,
  y,
  floatingStyles,
  update,
  placement: resolvedPlacement,
} = useFloating(referenceRef, popoverRef, {
  placement,
  middleware,
  strategy: "fixed",
});

// While scrolling, `transform` snaps to the anchor every frame, so a flip glides on the separate
// `translate` property instead; "add" stacks the remaining offsets when flips overlap.
watch([x, y, resolvedPlacement], ([newX, newY, newPlacement], [oldX, oldY, oldPlacement]) => {
  if (newPlacement === oldPlacement || !enableTransition.value || !scrolling.value) return;
  popoverRef.value?.animate([{ translate: `${oldX - newX}px ${oldY - newY}px` }, { translate: "0px 0px" }], {
    duration: TRANSTION_DURATION,
    easing: "ease",
    composite: "add",
  });
});

watch(popoverRef, () => {
  if (popoverRef.value) {
    update();
  }
});

let stopAutoUpdate: (() => void) | undefined;
let openGeneration = 0;
watch(isOpenModel, (newValue, oldValue) => {
  if (newValue) {
    document.addEventListener("touchstart", handleTouchOutside, { capture: true, passive: true });
  } else {
    document.removeEventListener("touchstart", handleTouchOutside, { capture: true });
  }

  if (newValue !== oldValue) {
    if (isOpenModel.value) {
      isPositioning.value = true;
    }
    togglePopover({ force: isOpenModel.value });
  }
  if (!isOpenModel.value) {
    stopAutoUpdate?.();
    stopAutoUpdate = undefined;
    return;
  }
  stopAutoUpdate?.();
  resolveAnchor();
  if (isOpenModel.value && referenceRef.value && popoverRef.value) {
    const myGeneration = ++openGeneration;
    void nextTick(() => {
      requestAnimationFrame(() => {
        if (!isOpenModel.value || !referenceRef.value || !popoverRef.value) return;
        clearComputedSize();
        void Promise.resolve(update())
          .then(() => nextTick())
          .then(() => {
            if (myGeneration === openGeneration) {
              isPositioning.value = false;
              void nextTick(() => emit("positioned"));
            }
          });
        stopAutoUpdate = autoUpdate(referenceRef.value, popoverRef.value, update, {
          animationFrame: true,
        });
      });
    });
    return;
  }
  isPositioning.value = false;
});

function handleTriggerClick(event: MouseEvent) {
  if (props.trigger !== "hover") return;
  if (!isOpenModel.value) return;
  if (!(event.target as Element | null)?.closest("button[popovertarget], input[popovertarget]")) return;
  event.preventDefault();
}

function handlePointerDown(event: PointerEvent) {
  isTouchInteraction.value = event.pointerType !== "mouse";
}

function handleContextMenu(event: MouseEvent) {
  if (isTouchInteraction.value) event.preventDefault();
}

function handleTouchStart() {
  if (props.trigger !== "hover") return;

  touchStartTime.value = Date.now();
  touchMoved.value = false;
  longPressTimer.value = setTimeout(() => {
    if (!touchMoved.value) {
      open();
    }
  }, 500);
}

function handleTouchMove() {
  touchMoved.value = true;
  if (longPressTimer.value) clearTimeout(longPressTimer.value);
}

function handleTouchEnd(event: TouchEvent) {
  if (longPressTimer.value) clearTimeout(longPressTimer.value);
  if (touchStartTime.value && props.trigger === "hover") {
    const duration = Date.now() - touchStartTime.value;
    if (duration >= 500 && !touchMoved.value) {
      if (event.cancelable) {
        event.preventDefault();
      }
    }
  }
  touchStartTime.value = null;
  touchMoved.value = false;
}

const handleTouchOutside = (event: TouchEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node) && isOpenModel.value) {
    close();
  }
};

onUnmounted(() => {
  document.removeEventListener("touchstart", handleTouchOutside, { capture: true });
  stopAutoUpdate?.();
});
</script>

<style module lang="scss">
$transition-duration: 300ms;

.dropdown,
.dropdown * {
  cursor: default;
  position: relative;
  user-select: none;
}

.trigger {
  width: 100%;
}

.popover {
  // The browser's own [popover] padding, made explicit so SCADropdownDivider's fullWidth can offset it.
  --sca-dropdown-popover-padding: 0.25em;
  padding: var(--sca-dropdown-popover-padding);
  position: fixed;
  top: 0;
  left: 0;
  margin: 0;
  max-width: calc(100vw - 16px);
  max-height: 100vh;
  // Consumers that stretch .dropdown over a larger area (e.g. InputTags' dropdown-anchor
  // pattern) set pointer-events:none on that wrapper so the invisible reference box doesn't
  // steal clicks from content underneath. That inherits onto .popover too, so restore it here —
  // the popover itself must always stay clickable regardless of the anchor's own state.
  pointer-events: auto;

  // TODO: Find a fix for SAFARI!
  // min-height: 200px;

  border-radius: var(--border-radius-large);
  border: 1px solid var(--color-coolGreyA-p5);
  background: var(--color-interaction-background-modeless);

  box-shadow:
    0px 2px 2px -1px rgba(27, 35, 44, 0.04),
    0px 8px 16px -2px rgba(27, 36, 44, 0.12);

  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
}

.withTransition {
  transition: transform $transition-duration ease;
}

// visibility:hidden (not display:none) — content stays measurable for the first flip/position
// calculation while staying unseen until it lands. See the isPositioning watcher above.
.positioning {
  visibility: hidden;
}

.inner {
  transition:
    opacity $transition-duration ease,
    transform $transition-duration ease;
}

.inner.animate {
  opacity: 1;
  transform: translateY(0);
  @starting-style {
    opacity: 0;
    transform: translateY(-10px);
  }
}

.lessBorder {
  border-radius: var(--border-radius-100);
}

.noBorder {
  border: none;
}

.noPadding {
  --sca-dropdown-popover-padding: 0px;
  padding: 0 !important;
}

.shadowUpward {
  box-shadow:
    0px -2px 8px -1px #1b242c14,
    0px -2px 2px -1px #1b242c0a,
    7px 4px 4px 0px #00000040 !important;
}

.scrollIconTop,
.scrollIconBottom {
  position: absolute;
  justify-content: center;
  pointer-events: none;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}
.scrollIconBottom {
  top: unset;
  bottom: 0;
}

.hidden {
  display: none;
}

.content {
  // Read by SCADropdownDivider's fullWidth to reach the popover's edges.
  --sca-dropdown-padding-inline: var(--space-component-padding-small);
  padding: var(--space-component-padding-small);
}

.morePadding {
  --sca-dropdown-padding-inline: var(--space-component-padding-medium);
  padding: var(--space-component-padding-medium);
  padding-top: var(--space-component-padding-small);
}

.items {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-padding-xSmall);
}

.paddingUnset {
  --sca-dropdown-padding-inline: var(--space-component-padding-null);
  padding: var(--space-component-padding-null);
}

@media (max-width: 768px) {
  .popover {
    max-height: calc(100vh - 2 * var(--space-component-padding-medium)) !important;
    margin-top: var(--space-component-padding-medium);

    &.noMobileMargin {
      margin-top: 0;
    }
  }
}
</style>
